import { supabase } from '../lib/supabase'
import { logger } from '../utils/logger'
import type { BlissBasicLandingSettings } from '../config/blissBasicCatalog'
import type { BlissBasicOptionCategoryId } from '../config/blissBasicOptionCategories'

const log = logger.module('blissBasicCatalogAdmin')
const BUCKET = 'bliss-basic-gallery'

export type BlissBasicProductRow = {
  id: string
  tenant_id: string
  name: string
  description: string | null
  price_usd: number | null
  price_label: string | null
  active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export type BlissBasicGalleryPhotoRow = {
  id: string
  tenant_id: string
  product_id: string | null
  storage_path: string
  title: string | null
  description: string | null
  focal_point: string | null
  price_usd: number | null
  width: number | null
  height: number | null
  is_hero: boolean
  active: boolean
  option_category: BlissBasicOptionCategoryId | null
  in_stock: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export function photoPublicUrl(storagePath: string): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath)
  return data.publicUrl
}

export async function fetchBlissBasicProducts(tenantId: string): Promise<BlissBasicProductRow[]> {
  const { data, error } = await supabase
    .from('bliss_basic_products')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('sort_order', { ascending: true })
  if (error) {
    log.error('fetch products', error)
    throw error
  }
  return (data ?? []) as BlissBasicProductRow[]
}

export async function fetchBlissBasicGalleryPhotos(tenantId: string): Promise<BlissBasicGalleryPhotoRow[]> {
  const { data, error } = await supabase
    .from('bliss_basic_gallery_photos')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('sort_order', { ascending: true })
  if (error) {
    log.error('fetch photos', error)
    throw error
  }
  return (data ?? []) as BlissBasicGalleryPhotoRow[]
}

export async function upsertBlissBasicProduct(
  tenantId: string,
  input: Partial<BlissBasicProductRow> & { name: string },
): Promise<BlissBasicProductRow> {
  const payload = {
    tenant_id: tenantId,
    name: input.name,
    description: input.description ?? null,
    price_usd: input.price_usd ?? null,
    price_label: input.price_label ?? 'Starting at',
    active: input.active ?? true,
    sort_order: input.sort_order ?? 0,
  }
  if (input.id) {
    const { data, error } = await supabase
      .from('bliss_basic_products')
      .update(payload)
      .eq('id', input.id)
      .eq('tenant_id', tenantId)
      .select()
      .single()
    if (error) throw error
    return data as BlissBasicProductRow
  }
  const { data, error } = await supabase.from('bliss_basic_products').insert(payload).select().single()
  if (error) throw error
  return data as BlissBasicProductRow
}

export async function deleteBlissBasicProduct(tenantId: string, id: string): Promise<void> {
  const { error } = await supabase.from('bliss_basic_products').delete().eq('id', id).eq('tenant_id', tenantId)
  if (error) throw error
}

function readImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
      URL.revokeObjectURL(url)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read image dimensions'))
    }
    img.src = url
  })
}

export async function uploadBlissBasicGalleryPhoto(
  tenantId: string,
  file: File,
  meta: {
    title?: string
    description?: string
    focal_point?: string | null
    price_usd?: number | null
    product_id?: string | null
    sort_order?: number
    is_hero?: boolean
    option_category?: BlissBasicOptionCategoryId | null
    in_stock?: boolean
  },
): Promise<BlissBasicGalleryPhotoRow> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
  const safeExt = ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? ext : 'jpg'
  const objectPath = `${tenantId}/${crypto.randomUUID()}.${safeExt}`
  const { width, height } = await readImageSize(file)

  const { error: uploadError } = await supabase.storage.from(BUCKET).upload(objectPath, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || 'image/jpeg',
  })
  if (uploadError) throw uploadError

  if (meta.is_hero) {
    await supabase
      .from('bliss_basic_gallery_photos')
      .update({ is_hero: false })
      .eq('tenant_id', tenantId)
  }

  const { data, error } = await supabase
    .from('bliss_basic_gallery_photos')
    .insert({
      tenant_id: tenantId,
      storage_path: objectPath,
      title: meta.title ?? file.name.replace(/\.[^.]+$/, ''),
      description: meta.description ?? null,
      focal_point: meta.focal_point ?? null,
      price_usd: meta.price_usd ?? null,
      product_id: meta.product_id ?? null,
      width,
      height,
      is_hero: meta.is_hero ?? false,
      active: true,
      option_category: meta.option_category ?? null,
      in_stock: meta.in_stock ?? true,
      sort_order: meta.sort_order ?? 0,
    })
    .select()
    .single()
  if (error) throw error
  return data as BlissBasicGalleryPhotoRow
}

export async function updateBlissBasicGalleryPhoto(
  tenantId: string,
  id: string,
  patch: Partial<
    Pick<
      BlissBasicGalleryPhotoRow,
      | 'title'
      | 'description'
      | 'focal_point'
      | 'price_usd'
      | 'product_id'
      | 'active'
      | 'sort_order'
      | 'is_hero'
      | 'option_category'
      | 'in_stock'
    >
  >,
): Promise<BlissBasicGalleryPhotoRow> {
  if (patch.is_hero) {
    await supabase.from('bliss_basic_gallery_photos').update({ is_hero: false }).eq('tenant_id', tenantId)
  }
  const { data, error } = await supabase
    .from('bliss_basic_gallery_photos')
    .update(patch)
    .eq('id', id)
    .eq('tenant_id', tenantId)
    .select()
    .single()
  if (error) throw error
  return data as BlissBasicGalleryPhotoRow
}

export async function deleteBlissBasicGalleryPhoto(
  tenantId: string,
  row: BlissBasicGalleryPhotoRow,
): Promise<void> {
  const { error: dbErr } = await supabase
    .from('bliss_basic_gallery_photos')
    .delete()
    .eq('id', row.id)
    .eq('tenant_id', tenantId)
  if (dbErr) throw dbErr
  const { error: storageErr } = await supabase.storage.from(BUCKET).remove([row.storage_path])
  if (storageErr) log.warn('storage remove failed', storageErr)
}

export function parseLandingSettings(settings: Record<string, unknown>): BlissBasicLandingSettings {
  const raw = (settings.blissBasicLanding ?? {}) as Record<string, unknown>
  return {
    startingPricePrefix:
      typeof raw.startingPricePrefix === 'string' ? raw.startingPricePrefix : 'Starting at',
    startingPriceUsd: typeof raw.startingPriceUsd === 'number' ? raw.startingPriceUsd : 3800,
    investmentNote:
      typeof raw.investmentNote === 'string'
        ? raw.investmentNote
        : 'An elevated solution. Exceptional value.',
  }
}
